
import React, { Component } from 'react';
import { Button } from '@massds/mayflower-react';
import {
  ComposableMap, ZoomableGroup, Geographies, Geography, Annotation
} from 'react-simple-maps';
import { scaleQuantile } from 'd3-scale';
import { schemePuBuGn } from 'd3-scale-chromatic';
import axios from 'axios';
import csv from 'csvtojson';
import ReactTooltip from 'react-tooltip';
import { quantile, ascending } from 'd3-array';
import LegendQuantile from '../Legend/Quantile';
import { customFormat2 } from '../helper';
import './Map.css';

import masstowns from '../data/masstowns.topo.json';

const colorScaleQuant = scaleQuantile();
const colorBostonScale = scaleQuantile()
  .domain([0.00, 0.45, 0.96, 1.49, 2.49, 3.49, 4.76, 10.37, 64.49])
  .range(schemePuBuGn[9]);

class OriginTripsCapMap extends Component {
  constructor() {
    super();
    this.state = {
      allData: [],
      center: [-71.63627925574303, 42.13276308419552],
      zoom: 1,
      disablePan: true,
      colorScale: colorScaleQuant,
      annotation: ['Cambridge (64.49)', 1, 'Boston (56.53)', 1, 'Nantucket (18.10)', 1, 'Hadley (7.68)', 1, 'Amherst (7.52)', 1],
      areas: [
        {
          name: 'Greater Boston', coordinates: [-71.036279, 42.332763], zoom: 4, colorScale: colorBostonScale, annotation: ['', 0, '', 0, '', 0, '', 0, '', 0]
        },
        {
          name: 'Cape Cod', coordinates: [-70.388761, 41.681497], zoom: 2.15, colorScale: colorScaleQuant, annotation: ['', 0, '', 0, '', 0, '', 0, '', 0]
        },
        {
          name: 'North Shore', coordinates: [-70.982613, 42.655939], zoom: 4, colorScale: colorScaleQuant, annotation: ['', 0, '', 0, '', 0, '', 0, '', 0]
        },
        {
          name: 'Central Mass', coordinates: [-72.217152, 42.419665], zoom: 2.5, colorScale: colorScaleQuant, annotation: ['', 0, '', 0, '', 0, '', 0, '', 0]
        },
        {
          name: 'Western Mass', coordinates: [-73.014503, 42.399396], zoom: 3, colorScale: colorScaleQuant, annotation: ['', 0, '', 0, '', 0, '', 0, '', 0]
        }
      ]
    };
    this.handleAreaSelection = this.handleAreaSelection.bind(this);
    this.handleReset = this.handleReset.bind(this);
  }

  componentDidMount() {
    this.fetchData();
  }

  parseData = (data) => {
    const allData = [];
    csv({
      noheader: false
    }).fromString(data)
      .subscribe((json) => {
        allData.push(json);
      })
      .on('done', () => {
        this.setState({ allData });
      })
      .on('error', (err) => {
        // eslint-disable-next-line no-console
        console.log(err);
      });
  }

  fetchData = () => {
    const _this = this;
    axios.all([axios.get('../data/Rideshare_stats_v4.csv')])
      .then(axios.spread((result) => {
        const { data } = result;
        _this.parseData(data);
      }))
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log(error);
      });
  };

  handleAreaSelection(e, i) {
    const { areas } = this.state;
    const area = areas[i];
    this.setState({
      center: area.coordinates,
      zoom: area.zoom,
      annotation: area.annotation,
      colorScale: area.colorScale,
      name: area.name,
      disablePan: false
    });
  }

  handleReset() {
    this.setState({
      center: [-71.63627925574303, 42.13276308419552],
      zoom: 1,
      annotation: ['Cambridge (64.49)', 1, 'Boston (56.53)', 1, 'Nantucket (18.10)', 1, 'Hadley (7.68)', 1, 'Amherst (7.52)', 1],
      colorScale: colorScaleQuant,
      name: 'Massachusetts',
      disablePan: true
    });
  }

  render() {
    const {
      allData, areas, center, zoom, disablePan, annotation, colorScale, name
    } = this.state;
    if (allData.length === 0) {
      return<div className="container">Loading...</div>;
    }
    const oriArray = allData.map((item) => Number(item.ORIGIN_TRIPS_PER_PERSON));
    oriArray.sort(ascending);
    const breaks = [0.00, quantile(oriArray, 0.125), quantile(oriArray, 0.25), quantile(oriArray, 0.375), quantile(oriArray, 0.5), quantile(oriArray, 0.625), quantile(oriArray, 0.75), quantile(oriArray, 0.875), quantile(oriArray, 1.00)];
    if (name !== 'Greater Boston') {
      colorScale.domain(breaks).range(schemePuBuGn[9]);
    }

    return(
      <div>
        <div className="wrapperButtonStyles">
          {
            areas.map((area, i) => (
              <Button
                size="small"
                info="Zoom to area on map."
                text={area.name}
                onClick={(e) => this.handleAreaSelection(e, i)}
                usage="secondary"
                // eslint-disable-next-line react/no-array-index-key
                key={`originCapMap_button-${i}`}
              />
            ))
          }
          <Button
            size="small"
            info="Zoom out to full state."
            text="Reset"
            onClick={this.handleReset}
            usage="secondary"
            // eslint-disable-next-line react/no-array-index-key
            key="originCapMap_button_reset"
            theme="c-primary-alt"
          />
        </div>
        <div className="wrapperStyles">
          <div className="wrapperMapStyles">
            <ComposableMap
              projection="mercator"
              projectionConfig={{
                scale: 12000
              }}
              width={980}
              height={530}
              style={{
                width: '100%',
                height: 'auto'
              }}
            >
              <ZoomableGroup center={center} zoom={zoom} disablePanning={disablePan}>
                <Geographies geography={masstowns} disableOptimization>
                  {(geographies, projection) => geographies.map((geography, i) => {
                    const townData = allData.find((s) => s.FIPS_CODE === geography.id) || {};
                    const town = townData.TOWN;
                    const metric = townData.ORIGIN_TRIPS_PER_PERSON;
                    const fill = colorScale(metric);
                    return(
                      <Geography
                        // eslint-disable-next-line react/no-array-index-key
                        key={`originCapMap_button-${i}two`}
                        cacheId={`${i}two`}
                        round
                        geography={geography}
                        projection={projection}
                        data-html
                        data-tip={`<strong>${town}</strong><br><strong>Origin Trips</strong>: ${customFormat2(metric)} per person`}
                        style={{
                          default: {
                            fill,
                            stroke: '#dcdcdc',
                            strokeWidth: 0.5,
                            outline: 'none'
                          },
                          hover: {
                            fill,
                            stroke: 'rgba(255,255,255,1.0)',
                            fillOpacity: '0.7',
                            strokeWidth: 2.00,
                            outline: 'true'
                          },
                          pressed: {
                            fill,
                            stroke: 'rgba(255,255,255,1.0)',
                            fillOpacity: '0.3',
                            strokeWidth: 2.00,
                            outline: 'true'
                          }
                        }}
                      />
                    );
                  })}
                </Geographies>
                <Annotation
                  dx={80}
                  dy={-40}
                  subject={[-71.115787, 42.373960]}
                  strokeWidth={annotation[1]}
                  stroke="black"
                >
                  <text>{annotation[0]}</text>
                </Annotation>
                <Annotation
                  dx={80}
                  dy={0}
                  subject={[-71.055787, 42.333960]}
                  strokeWidth={annotation[3]}
                  stroke="black"
                >
                  <text>{annotation[2]}</text>
                </Annotation>
                <Annotation
                  dx={30}
                  dy={-40}
                  subject={[-70.232976, 41.285821]}
                  strokeWidth={annotation[5]}
                  stroke="black"
                >
                  <text>{annotation[4]}</text>
                </Annotation>
                <Annotation
                  dx={30}
                  dy={200}
                  subject={[-72.59, 42.39645]}
                  strokeWidth={annotation[7]}
                  stroke="black"
                >
                  <text>{annotation[6]}</text>
                </Annotation>
                <Annotation
                  dx={80}
                  dy={160}
                  subject={[-72.54422, 42.42781]}
                  strokeWidth={annotation[9]}
                  stroke="black"
                >
                  <text>{annotation[8]}</text>
                </Annotation>
              </ZoomableGroup>
            </ComposableMap>
            <ReactTooltip type="light" />
          </div>
          <div className="wrapperLegendStyles">
            <p className="legendTitle">Origin Trips per Person</p>
            <LegendQuantile
              scale={colorScale}
              direction="column"
              labelMargin="0 5px 0 0"
              shape="rect"
              labelFormat={customFormat2}
            />
          </div>
        </div>
      </div>
    );
  }
}

export default OriginTripsCapMap;
